import { useMemo, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Alert,
  Button,
  Card,
  ColorInput,
  CopyButton,
  Group,
  Image,
  Loader,
  Modal,
  SegmentedControl,
  Select,
  Slider,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconCheck,
  IconCopy,
  IconDownload,
  IconExternalLink,
  IconQrcode,
} from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';

/**
 * WhatsApp QR code generator — turns a connected WABA number into a scannable
 * `wa.me` deep link for print, packaging and storefront use.
 *
 * The QR is always encoded server-side (`whatsapp.qr.generate`) so what the
 * vendor previews, downloads, and embeds is byte-for-byte the same payload.
 * Style controls are debounced: dragging the size slider must not fire one
 * request per pixel.
 */

interface WhatsAppQrModalProps {
  opened: boolean;
  onClose: () => void;
}

const MESSAGE_MAX = 500;
const EC_LEVELS = ['L', 'M', 'Q', 'H'] as const;
type EcLevel = (typeof EC_LEVELS)[number];

function triggerDownload(href: string, fileName: string) {
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  link.click();
}

export function WhatsAppQrModal({ opened, onClose }: WhatsAppQrModalProps) {
  const [phoneNumberId, setPhoneNumberId] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [foregroundColor, setForegroundColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [size, setSize] = useState(350);
  const [errorCorrectionLevel, setErrorCorrectionLevel] = useState<EcLevel>('M');
  const [svgBusy, setSvgBusy] = useState(false);

  // One debounced snapshot of every style input — the query key only changes
  // once the vendor stops typing/dragging.
  const [debounced] = useDebouncedValue(
    { message, foregroundColor, backgroundColor, size, errorCorrectionLevel },
    300,
  );

  const numbers = trpc.whatsapp.qr.phoneNumbers.useQuery(undefined, { enabled: opened });
  const hasNumbers = (numbers.data?.length ?? 0) > 0;

  const options = useMemo(
    () => ({
      ...(phoneNumberId ? { phoneNumberId } : {}),
      ...(debounced.message.trim() ? { prefilledMessage: debounced.message.trim() } : {}),
      size: debounced.size,
      margin: 2,
      foregroundColor: debounced.foregroundColor,
      backgroundColor: debounced.backgroundColor,
      errorCorrectionLevel: debounced.errorCorrectionLevel,
      format: 'dataUri' as const,
    }),
    [phoneNumberId, debounced],
  );

  const qr = trpc.whatsapp.qr.generate.useQuery(options, { enabled: opened && hasNumbers });
  const utils = trpc.useUtils();

  const fileStem = qr.data
    ? `whatsapp-qr-${qr.data.displayPhoneNumber.replace(/\D/g, '')}`
    : 'whatsapp-qr';

  // SVG is fetched on demand rather than kept in a second live query — it is
  // only ever wanted at the moment someone clicks Download.
  const downloadSvg = async () => {
    setSvgBusy(true);
    try {
      const res = await utils.whatsapp.qr.generate.fetch({ ...options, format: 'svg' });
      if (!res.svg) return;
      const url = URL.createObjectURL(new Blob([res.svg], { type: 'image/svg+xml' }));
      triggerDownload(url, `${fileStem}.svg`);
      URL.revokeObjectURL(url);
    } finally {
      setSvgBusy(false);
    }
  };

  const embedSnippet = qr.data
    ? `<img src="${qr.data.publicImageUrl}" alt="Chat with us on WhatsApp" width="350" height="350" />`
    : '';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      centered
      title={
        <Group gap="xs">
          <IconQrcode size={20} />
          <Text fw={600}>WhatsApp QR code</Text>
        </Group>
      }
    >
      {numbers.isLoading ? (
        <Stack align="center" py="xl">
          <Loader />
        </Stack>
      ) : !hasNumbers ? (
        <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
          Connect a WhatsApp number first — the QR code points at one of your numbers.
        </Alert>
      ) : (
        <Stack gap="md">
          <Group align="flex-start" grow>
            <Stack gap="sm">
              <Select
                label="Phone number"
                data={(numbers.data ?? []).map((n) => ({
                  value: n.phoneNumberId,
                  label: n.verifiedName
                    ? `${n.displayPhoneNumber} · ${n.verifiedName}`
                    : n.displayPhoneNumber,
                }))}
                value={phoneNumberId ?? numbers.data?.[0]?.phoneNumberId ?? null}
                onChange={setPhoneNumberId}
                allowDeselect={false}
              />
              <Textarea
                label="Pre-filled message"
                description="Appears in the customer's chat box after they scan."
                placeholder="Hi! I saw your QR code and would like to know more."
                value={message}
                maxLength={MESSAGE_MAX}
                onChange={(e) => setMessage(e.currentTarget.value)}
                autosize
                minRows={3}
                maxRows={5}
              />
            </Stack>

            <Stack align="center" justify="center" gap="xs">
              <Card withBorder p="md" radius="md" style={{ backgroundColor }}>
                {qr.isLoading || !qr.data?.dataUri ? (
                  <Loader size="sm" />
                ) : (
                  <Image
                    src={qr.data.dataUri}
                    alt="WhatsApp QR code"
                    w={200}
                    h={200}
                    fit="contain"
                  />
                )}
              </Card>
              <Text size="xs" c="dimmed" ta="center">
                {qr.data?.verifiedName ?? qr.data?.displayPhoneNumber ?? ''}
              </Text>
            </Stack>
          </Group>

          {qr.error && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {qr.error.message}
            </Alert>
          )}

          <Accordion variant="contained">
            <Accordion.Item value="style">
              <Accordion.Control>Appearance</Accordion.Control>
              <Accordion.Panel>
                <Stack gap="sm">
                  <Group grow>
                    <ColorInput
                      label="Foreground"
                      format="hex"
                      value={foregroundColor}
                      onChange={setForegroundColor}
                    />
                    <ColorInput
                      label="Background"
                      format="hex"
                      value={backgroundColor}
                      onChange={setBackgroundColor}
                    />
                  </Group>
                  <div>
                    <Text size="sm" fw={500} mb={4}>
                      Size — {size}px
                    </Text>
                    <Slider min={128} max={2048} step={50} value={size} onChange={setSize} />
                  </div>
                  <div>
                    <Text size="sm" fw={500} mb={4}>
                      Error correction
                    </Text>
                    <SegmentedControl
                      fullWidth
                      value={errorCorrectionLevel}
                      onChange={(v) => setErrorCorrectionLevel(v as EcLevel)}
                      data={[...EC_LEVELS]}
                    />
                    <Text size="xs" c="dimmed" mt={4}>
                      Higher levels stay scannable when the print is damaged or partly covered.
                    </Text>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          {qr.data && (
            <Stack gap="xs">
              <CopyableField label="WhatsApp link" value={qr.data.targetUrl} />
              <CopyableField label="Image URL" value={qr.data.publicImageUrl} />
              <CopyableField label="Embed code" value={embedSnippet} />
            </Stack>
          )}

          <Group justify="space-between">
            <Button
              component="a"
              href={qr.data?.targetUrl ?? '#'}
              target="_blank"
              rel="noreferrer"
              variant="default"
              disabled={!qr.data}
              leftSection={<IconExternalLink size={16} />}
            >
              Test chat
            </Button>
            <Group gap="xs">
              <Button
                variant="default"
                leftSection={<IconDownload size={16} />}
                loading={svgBusy}
                disabled={!qr.data}
                onClick={downloadSvg}
              >
                SVG
              </Button>
              <Button
                leftSection={<IconDownload size={16} />}
                disabled={!qr.data?.dataUri}
                onClick={() =>
                  qr.data?.dataUri && triggerDownload(qr.data.dataUri, `${fileStem}.png`)
                }
              >
                PNG
              </Button>
            </Group>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

function CopyableField({ label, value }: { label: string; value: string }) {
  return (
    <TextInput
      label={label}
      value={value}
      readOnly
      rightSection={
        <CopyButton value={value}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? 'Copied' : 'Copy'}>
              <ActionIcon variant="subtle" color={copied ? 'teal' : 'gray'} onClick={copy}>
                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      }
    />
  );
}
